import type { IKeytipProps } from "@fluentui/react/lib/Keytip";

import type { AppButtonProps } from "@1js/acui-button";
import type {
  AppColors,
  AppStrings,
  MenuOpenOnExecuteParameter,
} from "@1js/acui-common";
import type { AppIcons } from "@1js/acui-icons";
import type { PivotHeaderExecutePayload } from "@1js/acui-pivot";

import type {
  RibbonControlProps,
  RibbonFlyoutAnchorProps,
} from "../../Controls/RibbonControlProps";
import type { RibbonScalingStep } from "../../Scaling";
import type {
  RibbonModeToggleProps,
  RibbonPaddleProps,
} from "../RibbonBottomBar";
import type { Layout } from "../RibbonControlGroupMLR/layoutTypings";
import type { TabType } from "../TabList/TabList.Props";
import type {
  RibbonBottomBarStylingConstants,
  RibbonTopBarStylingConstants,
} from "../../ribbonStylingConstants";
import type { AppControlProps, ControlRendererFunction } from "../../../..";

export interface RibbonStateProps {
  /**
   * The Ribbon Tabs to display in this Ribbon
   */
  ribbonTabs: RibbonTabDefinitionSet;

  /**
   * The ID of the currently selected Ribbon Tab.
   * If set, the external app is controlling Tab switching instead of the Ribbon component.
   */
  selectedTabId?: string;

  /**
   * The ID of the tab that should be initially selected.
   * This is set when the external app wants to allow the Ribbon to drive Tab switching.
   */
  defaultSelectedTabId?: string;

  /**
   * Provides the app-specific colors to the control
   */
  appColors: AppColors;

  /**
   * The icons that are required by the Ribbon framework
   */
  appIcons: AppIcons;

  /**
   * Common static localized strings used
   * in different places in the ribbon
   */

  appStrings: AppStrings;

  /**
   * True if the TopBar TabList should calculate and display the dynamic pivot indicator. If false, a less-costly placeholder will be used
   */
  displayDynamicTabHeaderIndicator?: boolean;

  /**
   * Option to disable animations for the ribbon bottom bar and control groups
   */
  disableAnimations?: boolean;

  /**
   * Option to opt out of memoization of ribbon menu items. Will result in slower menu open times but necessary if your command handlers rely on internal references
   */
  skipMemoization?: boolean;

  /**
   * The function that will be utilized for rendering peripheral controls.
   */
  peripheralControlRendererFunction?: ControlRendererFunction;

  /**
   * The peripheral controls props that will be rendered by peripheralControlRendererFunction.
   */
  peripheralControls?: AppControlProps[];
}

export interface RibbonComponentStylingConstants {
  topBarConstants?: RibbonTopBarStylingConstants;
  bottomBarConstants?: RibbonBottomBarStylingConstants;
}
export interface RibbonDispatchProps {
  /**
   * Function that is called when a Ribbon Tab header is clicked. It provides the id of the Ribbon Tab header that was clicked.
   */
  onTabHeaderClick?: (
    tabId: string,
    payload?: PivotHeaderExecutePayload
  ) => void;

  /**
   * Props to define the behavior of the Ribbon Mode Toggle button
   */
  ribbonModeToggleProps?: RibbonModeToggleProps;

  /**
   * Props to define the overflow paddle buttons
   */
  ribbonPaddleProps?: RibbonPaddleProps;

  /**
   * Styling constants that will override the defaults for the ribbon bottom or top bar components
   */
  customStylingConstants?: RibbonComponentStylingConstants;
}

export interface RibbonOwnProps {
  /**
   * Option to opt out of memoization of ribbon menu items. Will result in slower menu open times but necessary if your command handlers rely on internal references
   */
  skipMemoization?: boolean;

  /**
   * Do we want to prevent the focus trap zone from keeping focus within the ribbon on tab.
   */
  preventTrapFocus?: boolean;
}

/**
 * The API for the top level Ribbon component
 */
export type RibbonProps = RibbonStateProps &
  RibbonDispatchProps &
  RibbonOwnProps;

export type RibbonMode = RibbonModeSingleLine | RibbonModeMultiline;
export type RibbonModeSingleLine = "SingleLine";
export type RibbonModeMultiline = "Multiline";
export type RibbonModeToolBar = "ToolBar";

/**
 * Describes a set of RibbonTabDefinitions to be used by the ribbon when rendering a given mode: single or multi-line
 */
export interface RibbonTabDefinitionSet {
  tabs: RibbonTabDefinitionBase[];
}

/**
 * Describes a set of RibbonTabDefinitions to be used by the ribbon when rendering in single-line mode
 */
export interface RibbonTabDefinitionSetSLR extends RibbonTabDefinitionSet {
  type: RibbonModeSingleLine;
  tabs: RibbonTabDefinitionSLR[];
}

/**
 * Describes a set of RibbonTabDefinitions to be used by the ribbon when rendering in multi-line mode
 */
export interface RibbonTabDefinitionSetMLR extends RibbonTabDefinitionSet {
  type: RibbonModeMultiline;
  tabs: RibbonTabDefinitionMLR[];
}

/**
 * Describes a set of controls, organized into groups, that are associated with a Ribbon Tab
 */
export interface RibbonTabDefinitionBase {
  type: RibbonMode;
  /**
   * The unique id of this tab
   */
  id: string;

  /**
   * The title to display for the Ribbon tab
   */
  title: string;

  /**
   * The type of tab. Used to make sure the contextual tabs are styled differently. If no type is provided, the tab is assumed
   * to be of default type.
   */
  tabType?: TabType;

  /**
   * The control groups that should appear when this tab is active
   */
  controlGroups: RibbonControlGroupDefinitionSetBase;

  /**
   * An ordered list of steps to apply in order to reduce the width of
   *  the controls so that they fit in the browser window.
   */
  scalingSteps?: Readonly<RibbonScalingStep>[];

  /**
   * Scaling steps that should be always applied before the scaling steps are applied. These transformations
   * will always be applied, regardless of how much Ribbon width is still available. Typical use cases include
   * controls that always appear in an overflow well or controls that don't have a label on the top level.
   */
  initialScalingSteps?: Readonly<RibbonScalingStep>[];

  /**
   * Optional Keytip for this Ribbon tab
   */
  keytipProps?: IKeytipProps;
}

/**
 * Describes a set of controls, organized into groups, that are associated with a Ribbon Tab when rendering in single-line mode
 */
export interface RibbonTabDefinitionSLR extends RibbonTabDefinitionBase {
  type: RibbonModeSingleLine;
  controlGroups: RibbonControlGroupDefinitionSetSLR;
}

/**
 * Describes a set of controls, organized into groups, that are associated with a Ribbon Tab when rendering in multi-line mode
 */
export interface RibbonTabDefinitionMLR extends RibbonTabDefinitionBase {
  type: RibbonModeMultiline;
  controlGroups: RibbonControlGroupDefinitionSetMLR;
}

/**
 * Describes a set of control groups that can appear in a Ribbon tab
 */
export interface RibbonControlGroupDefinitionSetBase {
  type: RibbonMode;
  groups: RibbonControlGroupDefinitionBase[];
}

/**
 * Describes a set of control groups that can appear in a Ribbon tab in single-line mode
 */
export interface RibbonControlGroupDefinitionSetSLR
  extends RibbonControlGroupDefinitionSetBase {
  type: RibbonModeSingleLine;
  groups: RibbonControlGroupDefinitionSLR[];
}

/**
 * Describes a set of control groups that can appear in a Ribbon tab in multi-line mode
 */
export interface RibbonControlGroupDefinitionSetMLR
  extends RibbonControlGroupDefinitionSetBase {
  type: RibbonModeMultiline;
  groups: RibbonControlGroupDefinitionMLR[];
}

export type RibbonControlGroupDefinitionSet =
  | RibbonControlGroupDefinitionSetSLR
  | RibbonControlGroupDefinitionSetMLR;

/**
 * Describes a single control group that can appear in a Ribbon tab
 */
export interface RibbonControlGroupDefinitionBase {
  type: RibbonMode;
  /**
   * A unique id for the control group
   */
  id: string;

  /**
   * The title to display for this group of controls
   */
  title: string;

  /**
   * The set of controls that appear in this group
   */
  controls: RibbonControlProps[];

  /**
   * Optional keytip for this control group, will be used on the overflow button
   */
  keytipProps?: IKeytipProps;

  /**
   * If true, the controls in this ContorlGroup will
   * be fast disabled
   */
  fastDisabled?: boolean;
}

/**
 * Describes a single control group that can appear in a Ribbon tab in single-line mode
 */
export interface RibbonControlGroupDefinitionMLR
  extends RibbonControlGroupDefinitionBase {
  type: RibbonModeMultiline;
  /** The set of available control arrangements for this group. Ordered from largest to smallest. If unspecified, controls will be drawn full-height in a single row.
   * If multiple layouts are specified, the first one will be chosen. You can iterate towards smaller layouts by specifying ScaleGroup scaling steps.
   */
  layouts?: Layout[];

  /**
   * Available control arrangements for this group with all layouts combined for responsive scaling. If unspecified, controls will fallback to layouts.
   */
  responsiveLayout?: Layout;

  /** The button properties to render the group launcher for an Multiline Ribbon control group.  If the props are not present the launcher will not be shown  */
  groupLauncher?: AppButtonProps;

  /**
   * True if the group label should be hidden. It's used by scaled groups, that have been collapsed together. Impacts other scaling logic, besides just hiding the label.
   */
  hideGroupLabel?: boolean;

  /**
   * True if the group label should be hidden from the UX, but no other changes caused by this. Can be used by regular control groups, not just scaled/collapsed groups
   */
  forceHideGroupLabelOnly?: boolean;

  /**
   * Optional props for the flyout anchor used to hold all controls in the group. This is used once the "ConvertGroupToFlyout" scaling step is defined and activated
   */
  commonGroupFlyout?: RibbonFlyoutAnchorProps;

  /**
   * VSO:4229595 - Temporary flag to disable animations for this control group.
   */
  disableAnimations?: boolean;
}

/**
 * Describes a single control group that can appear in a Ribbon tab in multi-line mode
 */
export interface RibbonControlGroupDefinitionSLR
  extends RibbonControlGroupDefinitionBase {
  type: RibbonModeSingleLine;

  /**
   * Set to true if the overflow menu launcher should be disabled
   */
  overflowMenuLauncherDisabled?: boolean;

  /**
   * The label for the overflow menu launcher for this group. This will be surfaced to the user as a tooltip.
   */
  overflowMenuLauncherLabel: string;

  /**
   * Function that gets invoked when the overflow button action is executed by the user.
   * id is the group id.
   */
  onOverflowMenuOpen?: (parameter: MenuOpenOnExecuteParameter) => void;

  /**
   * Function that gets invoked on overflow MenuDismiss.
   * The callback provides the id of the component that invoked the event.
   */
  onOverflowMenuClose?: (id: string) => void;

  /**
   * Set to true if the overflow menu items generated by this control group should be kept in the menu
   * next to this control group instead of moved and merged into an overflow menu at the end of the whole ribbon
   */
  hasOwnOverflow?: boolean;

  /**
   * Set to true if all the controls in a Group can be moved to overflow
   */
  shouldAllowAllControlsInOverflow?: boolean;
}

/**
 * Describes a single control group that can appear in a Ribbon tab
 */
export type RibbonControlGroupDefinition =
  | RibbonControlGroupDefinitionMLR
  | RibbonControlGroupDefinitionSLR;

/**
 * Gets the id for the bottom bar.
 * This value is used for the id of the bottom bar
 * @param tabId - the string to create the bottom bar id with.
 * This should be the {@linkcode RibbonTabDefinition.id} of the associated tab
 */
export function getRibbonBottomBarId(tabId: string): string {
  return `${tabId}-panel`;
}

/**
 * Gets the id for the bottom bar.
 * This value is used for the aria-control on the tab. It is similar
 * to getRibbonBottomBarId but needed with a different ID so we don't break
 * scaling and automation
 */
export function getRibbonBottomBarAriaControlsId(tabId: string): string {
  return `${tabId}-tabpanel`;
}
